import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ArrowRight, 
  ArrowLeft, 
  Calendar, 
  Clock, 
  CheckCircle, 
  Globe, 
  Loader2,
  ChevronLeft,
  ChevronRight,
  User,
  Mail,
  Phone,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('questions');
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [availability, setAvailability] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [userTimezone, setUserTimezone] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Settings from backend
  const [adminTimezone, setAdminTimezone] = useState('Asia/Dhaka');
  const [timeFormat, setTimeFormat] = useState('12');
  
  const [userDetails, setUserDetails] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('questions');
      setCurrentQuestionIndex(0);
      setAnswers({});
      setSelectedDate(null);
      setSelectedSlot(null);
      setBookingResult(null);
      setUserDetails({ name: '', email: '', phone: '' });
      fetchQuestions();
      fetchSettings();
    }
  }, [isOpen]);

  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setUserTimezone(tz);
  }, []);

  useEffect(() => {
    if (step === 'calendar' && isOpen) {
      fetchAvailability();
    }
  }, [step, isOpen]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/settings`);
      const data = await res.json();
      setAdminTimezone(data.admin_timezone || 'Asia/Dhaka');
      setTimeFormat(data.time_format || '12');
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchQuestions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/booking/questions`);
      const data = await res.json();
      setQuestions(data);
      setLoading(false);
      if (data.length === 0) {
        setStep('calendar');
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setLoading(false);
      setStep('calendar');
    }
  };

  const fetchAvailability = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/booking/availability`);
      const data = await res.json();
      setAvailability(data);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
    setLoading(false);
  };

  // Get timezone offset in minutes
  const getTimezoneOffsetMinutes = (tz, date) => {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: tz }));
    return (tzDate - utcDate) / 60000;
  };

  // Convert admin time to user's local time
  const convertAdminTimeToUserTime = (time, date) => {
    try {
      const [hours, minutes] = time.split(':');
      const dateStr = `${date}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
      
      const adminTime = new Date(dateStr);
      const adminOffset = getTimezoneOffsetMinutes(adminTimezone, adminTime);
      const utcTime = new Date(adminTime.getTime() - adminOffset * 60000);
      
      const userOffset = getTimezoneOffsetMinutes(userTimezone, utcTime);
      const userTime = new Date(utcTime.getTime() + userOffset * 60000);
      
      const h = userTime.getHours();
      const m = userTime.getMinutes();
      
      if (timeFormat === '12') {
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
      }
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    } catch (e) {
      console.error('Time conversion error:', e);
      return time;
    }
  };

  // Get user-friendly timezone display
  const getUserTimezoneDisplay = () => {
    try {
      const now = new Date();
      const offset = -now.getTimezoneOffset();
      const hours = Math.floor(Math.abs(offset) / 60);
      const mins = Math.abs(offset) % 60;
      const sign = offset >= 0 ? '+' : '-';
      const utcOffset = `UTC${sign}${hours}${mins > 0 ? ':' + String(mins).padStart(2, '0') : ''}`;
      const tzName = userTimezone.split('/').pop().replace(/_/g, ' ');
      return `${tzName} (${utcOffset})`;
    } catch (e) {
      return userTimezone;
    }
  };

  // Keep old convertTime for backward compatibility (but use new function)
  const convertTime = (time, date) => convertAdminTimeToUserTime(time, date);

  const handleAnswer = (value) => {
    const question = questions[currentQuestionIndex];
    setAnswers(prev => ({ ...prev, [question.id]: value }));
  };

  const nextQuestion = () => {
    const question = questions[currentQuestionIndex];
    if (question.required && !answers[question.id]) {
      alert('Please answer this question to proceed.');
      return;
    }
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setStep('calendar');
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleDateSelect = (dateDoc) => {
    setSelectedDate(dateDoc);
    setSelectedSlot(null);
    setStep('slots');
  };

  const handleSlotSelect = async (slot) => {
    setSelectedSlot(slot);
    try {
      await fetch(`${BACKEND_URL}/api/booking/lock-slot?date_id=${selectedDate.id}&slot_id=${slot.id}`, {
        method: 'POST'
      });
    } catch (e) {
      console.error('Error locking slot:', e);
    }
    setStep('details');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userDetails.name || !userDetails.email) {
      alert('Please fill in your name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/booking/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date_id: selectedDate.id,
          slot_id: selectedSlot.id,
          user_name: userDetails.name,
          user_email: userDetails.email,
          user_phone: userDetails.phone,
          user_timezone: userTimezone,
          questionnaire_answers: answers
        })
      });
      const data = await res.json();
      if (data.success) {
        setBookingResult(data.booking);
        setStep('confirmation');
      } else {
        alert(data.detail || 'Booking failed. Please try again.');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('An error occurred. Please try again.');
    }
    setSubmitting(false);
  };

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      const availableDate = availability.find(d => d.date === dateStr && d.slots.length > 0);
      days.push({
        day: i,
        dateStr,
        available: !!availableDate,
        dateDoc: availableDate
      });
    }
    return days;
  }, [currentMonth, availability]);

  const renderQuestion = (question) => {
    const value = answers[question.id] || '';
    switch (question.question_type) {
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleAnswer(e.target.value)}
            className="w-full h-28 px-4 py-3 bg-background border border-border rounded-xl focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none text-foreground text-sm"
            placeholder="Type your answer..."
          />
        );
      case 'select':
      case 'radio':
        return (
          <div className="space-y-2">
            {question.options?.map((option, idx) => (
              <motion.button
                key={idx}
                type="button"
                whileTap={{ scale: 0.98 }}
                onClick={() => handleAnswer(option)}
                className={`w-full p-3 text-left rounded-xl border transition-all font-semibold text-sm ${
                  value === option
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border hover:border-primary/50 text-muted-foreground'
                }`}
              >
                {option}
              </motion.button>
            ))}
          </div>
        );
      default:
        return (
          <Input
            type="text"
            value={value}
            onChange={(e) => handleAnswer(e.target.value)}
            className="w-full h-12"
            placeholder="Type your answer..."
          />
        );
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto glass-strong rounded-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Header */}
          <div className="p-6 pb-0">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <Globe className="w-3 h-3" />
              <span>{getUserTimezoneDisplay()}</span>
            </div>
            {/* Progress */}
            <div className="flex items-center gap-1 mb-4">
              {['questions', 'calendar', 'slots', 'details', 'confirmation'].map((s, idx) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-all ${
                    step === s ? 'bg-primary' : 
                    ['questions', 'calendar', 'slots', 'details', 'confirmation'].indexOf(step) > idx 
                      ? 'bg-primary' 
                      : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="p-6 pt-2">
            {loading && step === 'questions' ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <AnimatePresence mode="wait">
                {/* Questions Step */}
                {step === 'questions' && questions.length > 0 && (
                  <motion.div
                    key="questions"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <span className="text-xs text-primary font-semibold">
                      Question {currentQuestionIndex + 1} of {questions.length}
                    </span>
                    <h2 className="text-lg font-bold text-foreground mt-1 mb-4">
                      {questions[currentQuestionIndex].question}
                    </h2>
                    <div className="mb-6">
                      {renderQuestion(questions[currentQuestionIndex])}
                    </div>
                    <div className="flex items-center justify-between">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={prevQuestion}
                        disabled={currentQuestionIndex === 0}
                      >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back
                      </Button>
                      <Button size="sm" onClick={nextQuestion} className="bg-gradient-primary">
                        {currentQuestionIndex === questions.length - 1 ? 'Continue' : 'Next'}
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Calendar Step */}
                {step === 'calendar' && (
                  <motion.div
                    key="calendar"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <div className="text-center mb-4">
                      <Calendar className="w-8 h-8 mx-auto text-primary mb-2" />
                      <h2 className="text-lg font-bold text-foreground">Select a Date</h2>
                    </div>
                    <div className="flex items-center justify-between mb-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm font-semibold text-foreground">
                        {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 mb-4">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                        <div key={i} className="text-center text-xs font-semibold text-muted-foreground py-1">
                          {day}
                        </div>
                      ))}
                      {calendarDays.map((day, idx) => (
                        <div key={idx} className="aspect-square">
                          {day ? (
                            <button
                              onClick={() => day.available && handleDateSelect(day.dateDoc)}
                              disabled={!day.available}
                              className={`w-full h-full rounded-lg flex items-center justify-center text-xs font-semibold transition-all ${
                                day.available
                                  ? 'bg-primary/10 text-primary hover:bg-primary hover:text-white'
                                  : 'text-muted-foreground/40'
                              }`}
                            >
                              {day.day}
                            </button>
                          ) : null}
                        </div>
                      ))}
                    </div>
                    {questions.length > 0 && (
                      <Button variant="ghost" size="sm" onClick={() => { setStep('questions'); setCurrentQuestionIndex(questions.length - 1); }}>
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back
                      </Button>
                    )}
                  </motion.div>
                )}

                {/* Slots Step */}
                {step === 'slots' && selectedDate && (
                  <motion.div
                    key="slots"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <div className="text-center mb-4">
                      <Clock className="w-8 h-8 mx-auto text-primary mb-2" />
                      <h2 className="text-lg font-bold text-foreground">Select a Time</h2>
                      <p className="text-xs text-muted-foreground font-semibold">
                        {new Date(selectedDate.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {selectedDate.slots.map((slot) => {
                        const isBooked = slot.status === 'booked';
                        return (
                          <button
                            key={slot.id}
                            onClick={() => !isBooked && handleSlotSelect(slot)}
                            disabled={isBooked}
                            className={`p-3 rounded-xl border transition-all text-center ${
                              isBooked 
                                ? 'border-red-500/30 bg-red-500/10 cursor-not-allowed' 
                                : 'border-border hover:border-primary hover:bg-primary/10'
                            }`}
                          >
                            <span className={`font-semibold text-sm ${isBooked ? 'text-muted-foreground line-through' : ''}`}>
                              {convertTime(slot.start_time, selectedDate.date)}
                            </span>
                            {isBooked && (
                              <span className="block text-xs text-red-500 font-bold mt-1">BOOKED</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setStep('calendar')}>
                      <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                  </motion.div>
                )}

                {/* Details Step */}
                {step === 'details' && (
                  <motion.div
                    key="details"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <div className="text-center mb-4">
                      <User className="w-8 h-8 mx-auto text-primary mb-2" />
                      <h2 className="text-lg font-bold text-foreground">Your Details</h2>
                      <p className="text-xs text-muted-foreground">
                        {new Date(selectedDate.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {convertTime(selectedSlot.start_time, selectedDate.date)}
                      </p>
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-foreground mb-1 block">Name *</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="text"
                            value={userDetails.name}
                            onChange={(e) => setUserDetails(prev => ({ ...prev, name: e.target.value }))}
                            className="pl-10 h-10"
                            placeholder="John Doe"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-foreground mb-1 block">Email *</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="email"
                            value={userDetails.email}
                            onChange={(e) => setUserDetails(prev => ({ ...prev, email: e.target.value }))}
                            className="pl-10 h-10"
                            placeholder="john@example.com"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-foreground mb-1 block">Phone (Optional)</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="tel"
                            value={userDetails.phone}
                            onChange={(e) => setUserDetails(prev => ({ ...prev, phone: e.target.value }))}
                            className="pl-10 h-10"
                            placeholder="+880 1234 567890"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            fetch(`${BACKEND_URL}/api/booking/release-slot?date_id=${selectedDate.id}&slot_id=${selectedSlot.id}`, { method: 'POST' });
                            setStep('slots');
                          }}
                        >
                          <ArrowLeft className="w-4 h-4 mr-1" /> Back
                        </Button>
                        <Button type="submit" size="sm" disabled={submitting} className="bg-gradient-primary">
                          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Confirm <CheckCircle className="w-4 h-4 ml-1" /></>}
                        </Button>
                      </div>
                    </form>
                  </motion.div>
                )}

                {/* Confirmation Step */}
                {step === 'confirmation' && bookingResult && (
                  <motion.div
                    key="confirmation"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring" }}
                      className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center"
                    >
                      <CheckCircle className="w-8 h-8 text-white" />
                    </motion.div>
                    <h2 className="text-xl font-bold text-foreground mb-2">Booking Confirmed!</h2>
                    <div className="bg-muted/30 rounded-xl p-4 text-left mb-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-primary" />
                        <span className="font-semibold">{new Date(bookingResult.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-primary" />
                        <span className="font-semibold">{convertTime(bookingResult.start_time, bookingResult.date)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4">Confirmation sent to {bookingResult.user_email}</p>
                    <Button onClick={onClose} className="bg-gradient-primary">Done</Button>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BookingModal;
